import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComment } from '@fortawesome/pro-solid-svg-icons';
import {
  selectNameAlertData,
  selectSensorsData,
  updateAlertName,
} from '../../../../redux/slices/alertFormSlice/alertFormSlice';
import { selectRoomAlertDeviceList, selectSensorMap } from '../../../../redux/slices/deviceList/deviceListSlice';
import { filterDevicesByChannelKeys, getChannelDisplayData } from '../../../../utility/channelUtils';
import { generateAlertNamePairs } from '../AlertFormUtility';
import AlertNamingModal from './AlertNamingKeywordModal';

function NameAlertInput() {
  const dispatch = useDispatch();

  const selectedChannels = useSelector(selectSensorsData);
  const selectedChannelList = selectedChannels ? selectedChannels.selectedChannels : [];
  const channelCount = selectedChannelList.length;

  const { alertNameInput } = useSelector(selectNameAlertData);

  const sensorMap = useSelector(selectSensorMap);
  const deviceList = useSelector(selectRoomAlertDeviceList);

  const inputRef = useRef(null);
  const [showModal, setShowModal] = useState(false);
  const [insertPosition, setInsertPosition] = useState({ start: 0, end: 0 });
  const selectedDevices = filterDevicesByChannelKeys(deviceList, selectedChannelList);
  const maxDeviceSensorSelected = selectedDevices.length && selectedDevices.find((device) => device.type === 'RAMAX');

  useEffect(() => {
    if (alertNameInput === '') {
      let defaultInput = '';
      if (channelCount > 1) {
        defaultInput = `${maxDeviceSensorSelected ? '[COMPONENT_NAME] ' : ''}[SENSOR_PORT] [SENSOR_NAME] [CHANNEL_TYPE] Alert`;
      }

      const pairs = generateAlertNamePairs(defaultInput,
        selectedChannelList, deviceList, sensorMap);

      dispatch(updateAlertName({
        alertNameInput: defaultInput,
        alertNamePairs: pairs,
      }));
    }
  }, [channelCount, alertNameInput, selectedChannelList, deviceList, sensorMap, dispatch]);

  const handleInputChange = (e) => {
    const newInput = e.target.value;

    const pairs = generateAlertNamePairs(newInput, selectedChannelList, deviceList, sensorMap);
    dispatch(updateAlertName({
      alertNameInput: newInput,
      alertNamePairs: pairs,
    }));
  };

  const getSuggestedName = () => {
    if (channelCount !== 1) {
      return '';
    }

    const channelKeyObj = {
      channelKey: selectedChannelList[0],
    };

    const {
      deviceName, sensorName, isRAMAXDevice, maxComponentLabel,
    } = getChannelDisplayData(channelKeyObj, deviceList, sensorMap);
    return `${deviceName}${isRAMAXDevice ? ` ${maxComponentLabel}` : ''} ${sensorName} Alert`;
  };

  const handleSuggestName = () => {
    const suggested = getSuggestedName();

    const pairs = generateAlertNamePairs(suggested, selectedChannelList, deviceList, sensorMap);

    dispatch(updateAlertName({
      alertNameInput: suggested,
      alertNamePairs: pairs,
    }));
  };

  const handleOpenModal = () => {
    const input = inputRef.current;
    if (input) {
      setInsertPosition({ start: input.selectionStart, end: input.selectionEnd });
    } else {
      setInsertPosition({ start: alertNameInput.length, end: alertNameInput.length });
    }
    setShowModal(true);
  };

  const handleKeywordSelect = (keyword) => {
    const newValue = alertNameInput.slice(0, insertPosition.start)
      + keyword + alertNameInput.slice(insertPosition.end);

    const pairs = generateAlertNamePairs(newValue, selectedChannelList, deviceList, sensorMap);

    dispatch(updateAlertName({
      alertNameInput: newValue,
      alertNamePairs: pairs,
    }));

    setTimeout(() => {
      const input = inputRef.current;
      if (input) {
        input.focus();
        const newPos = insertPosition.start + keyword.length;
        input.setSelectionRange(newPos, newPos);
      }
    }, 0);
  };

  return (
    <div>
      <div className="input-group">
        <input
          ref={inputRef}
          id="alertNameInput"
          type="text"
          className="form-control"
          value={alertNameInput}
          onChange={handleInputChange}
        />
        <div className="input-group-append ml-2">
          {channelCount > 1 ? (
            <button className="btn btn-secondary" type="button" onClick={handleOpenModal}>
              <FontAwesomeIcon
                icon={faComment}
                className="mr-1"
              />
              Insert keyword
            </button>
          ) : (
            <button
              className="btn btn-secondary"
              type="button"
              onClick={handleSuggestName}
            >
              Suggest name
            </button>
          )}
        </div>
      </div>
      <AlertNamingModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onSelect={handleKeywordSelect}
        hasMaxDevice={!!maxDeviceSensorSelected}
      />
    </div>
  );
}

export default NameAlertInput;
